import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { SEO } from "@/components/SEO";
import {
  CheckCircle2,
  Info,
  Calendar,
  XCircle,
  ArrowRight,
  Shield,
  TrendingUp,
  AlertTriangle,
} from "lucide-react";

export default function MetalVsAsphaltSaskatchewan() {
  const schema = [
    {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "BlogPosting",
          "@id":
            "https://www.saskroofingcompany.com/blog/metal-vs-asphalt-roofing-saskatchewan#article",
          headline:
            "Metal vs Asphalt Roofing in Saskatchewan: An Honest Comparison",
          description:
            "Trying to decide between a metal roof and asphalt shingles? We break down the costs, lifespan, and performance of both materials in Saskatchewan's extreme climate.",
          author: { "@id": "https://www.saskroofingcompany.com/#organization" },
          publisher: {
            "@id": "https://www.saskroofingcompany.com/#organization",
          },
          datePublished: "2026-04-15",
          inLanguage: "en-CA",
          isPartOf: {
            "@id": "https://www.saskroofingcompany.com/blog#webpage",
          },
          mainEntityOfPage:
            "https://www.saskroofingcompany.com/blog/metal-vs-asphalt-roofing-saskatchewan",
          url: "https://www.saskroofingcompany.com/blog/metal-vs-asphalt-roofing-saskatchewan",
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: "https://www.saskroofingcompany.com/",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Blog",
              item: "https://www.saskroofingcompany.com/blog",
            },
            {
              "@type": "ListItem",
              position: 3,
              name: "Metal vs Asphalt Roofing in Saskatchewan",
              item: "https://www.saskroofingcompany.com/blog/metal-vs-asphalt-roofing-saskatchewan",
            },
          ],
        },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "What is the best roofing material for Saskatchewan?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "The 'best' material depends on your timeline and budget. Architectural asphalt shingles are the best value for 20-30 years of protection. Standing seam metal is the ultimate long-term investment, offering 50+ years of durability against hail and snow.",
          },
        },
        {
          "@type": "Question",
          name: "Are metal roofs better than asphalt in Saskatchewan?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "In terms of pure durability, yes. Metal roofs shed snow effortlessly, preventing ice dams, and offer superior resistance to prairie hailstorms. However, they cost significantly more upfront than asphalt.",
          },
        },
        {
          "@type": "Question",
          name: "How much does metal roofing cost in Saskatchewan?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "A standing seam metal roof typically costs $15,000 to $30,000+ for an average Saskatchewan home, which is roughly two to three times the cost of an architectural asphalt shingle roof.",
          },
        },
        {
          "@type": "Question",
          name: "Will a metal roof handle Saskatchewan hail?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Yes. High-quality standing seam metal roofs are highly impact-resistant. While extreme, baseball-sized hail might cause cosmetic denting, it rarely compromises the structural integrity or waterproofing of a metal roof, unlike asphalt which can fracture.",
          },
        },
      ],
    },
  ];

  return (
    <div className="flex flex-col min-h-screen">
      <SEO
        title="Metal vs Asphalt Roofing Saskatchewan | Sask Roofing"
        description="Metal vs asphalt roofing in Saskatchewan: cost, lifespan, climate fit, warranty. An honest comparison from Sask Roofing."
        schema={schema}
      />

      {/* Hero Section */}
      <section className="relative py-32 text-primary-foreground overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/572054e1-5591-4b5f-8aec-9ad9f9db3559.png&output=webp&q=60"
            alt="Modern home showcasing a premium roof installation"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/40"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
        </div>
        <div className="container relative px-4 z-10">
          <div className="max-w-4xl space-y-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/80 text-primary-foreground text-sm font-medium mb-4">
              <Calendar className="w-4 h-4" /> Published April 15, 2026
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white drop-shadow-md">
              Metal vs Asphalt Roofing in Saskatchewan: An Honest Comparison
            </h1>
            <p className="text-xl text-white/90 drop-shadow-sm font-medium">
              It's the biggest roofing debate on the prairies. We break down the
              costs, lifespan, and performance of both materials so you can make
              the right call for your home.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-background">
        <div className="container px-4">
          <div className="max-w-4xl mx-auto space-y-12">
            {/* Intro */}
            <div className="prose prose-lg max-w-none text-muted-foreground">
              <p>
                If you are planning a{" "}
                <Link
                  to="/services/replacement"
                  className="text-primary hover:underline font-medium"
                >
                  roof replacement
                </Link>{" "}
                in Saskatchewan, you've likely found yourself caught in the
                middle of a heated debate: should you stick with traditional
                asphalt shingles, or make the leap to a premium metal roof?
              </p>
              <p>
                As a local contractor that installs both, we can tell you right
                now: this isn't a sales pitch for one over the other. Both
                materials have legitimate use cases, and the "right" choice
                depends entirely on your budget, how long you plan to stay in
                your home, and your neighborhood's specific exposure to prairie
                hailstorms and winter winds. Let's break down the facts.
              </p>
            </div>

            {/* TL;DR Box */}
            <div className="bg-primary/5 border-l-4 border-primary p-6 rounded-r-xl shadow-sm">
              <h2 className="flex items-center gap-2 text-xl font-bold text-foreground mb-3">
                <Info className="w-6 h-6 text-primary" />
                Quick Decision Framework (TL;DR)
              </h2>
              <ul className="space-y-2 text-lg text-muted-foreground">
                <li>
                  • <strong>Budget Focus + 20-Year Horizon:</strong> Choose
                  Architectural Asphalt.
                </li>
                <li>
                  • <strong>Investment Focus + 50-Year Horizon:</strong> Choose
                  Standing Seam Metal.
                </li>
                <li>
                  • <strong>Hail-Prone Neighborhood:</strong> Either works,
                  provided you upgrade to Class 4 Impact-Resistant asphalt or
                  heavy-gauge metal.
                </li>
                <li>
                  • <strong>Historic/District Restrictions:</strong> Always
                  verify with your HOA or municipality before committing to
                  metal.
                </li>
              </ul>
            </div>

            {/* Asphalt Section */}
            <div className="space-y-6">
              <h2 className="text-3xl font-bold text-foreground">
                Asphalt Shingle Roofing: The Prairie Standard
              </h2>
              <p className="text-lg text-muted-foreground">
                <Link
                  to="/services/asphalt"
                  className="text-primary hover:underline font-medium"
                >
                  Asphalt shingles
                </Link>{" "}
                protect the vast majority of homes in Saskatchewan, and for good
                reason. Modern architectural shingles are a massive upgrade from
                the flat 3-tab shingles of the past.
              </p>

              <div className="grid md:grid-cols-2 gap-6 my-8">
                {/* Pros Card */}
                <div className="bg-card p-6 rounded-xl border shadow-sm">
                  <h3 className="text-xl font-bold mb-4 flex items-center gap-2 text-green-700">
                    <CheckCircle2 className="w-6 h-6" />
                    Asphalt Pros
                  </h3>
                  <ul className="space-y-3 text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <span className="font-bold text-foreground mt-0.5">
                        •
                      </span>
                      <span>
                        <strong>Lower Upfront Cost:</strong> Significantly more
                        affordable, making it accessible for most budgets.
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="font-bold text-foreground mt-0.5">
                        •
                      </span>
                      <span>
                        <strong>Wide Color Palette:</strong> Endless options to
                        match your home's exterior design.
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="font-bold text-foreground mt-0.5">
                        •
                      </span>
                      <span>
                        <strong>Lighter Weight:</strong> Safe for older
                        structures that might not support heavier roofing
                        systems.
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="font-bold text-foreground mt-0.5">
                        •
                      </span>
                      <span>
                        <strong>Easier to Repair:</strong> Spot damage from
                        fallen branches or localized wind is straightforward and
                        cheap to fix.
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="font-bold text-foreground mt-0.5">
                        •
                      </span>
                      <span>
                        <strong>Solid Lifespan:</strong> A quality architectural
                        shingle typically lasts 20-30 years in Saskatchewan.
                      </span>
                    </li>
                  </ul>
                </div>

                {/* Cons Card */}
                <div className="bg-card p-6 rounded-xl border shadow-sm">
                  <h3 className="text-xl font-bold mb-4 flex items-center gap-2 text-red-700">
                    <XCircle className="w-6 h-6" />
                    Asphalt Cons
                  </h3>
                  <ul className="space-y-3 text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <span className="font-bold text-foreground mt-0.5">
                        •
                      </span>
                      <span>
                        <strong>Vulnerable to Extreme Hail:</strong> Standard
                        shingles can bruise or fracture in severe storms (though
                        Class 4 upgrades mitigate this).
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="font-bold text-foreground mt-0.5">
                        •
                      </span>
                      <span>
                        <strong>Shorter Lifecycle:</strong> You will likely need
                        to replace the roof roughly every 25 years.
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="font-bold text-foreground mt-0.5">
                        •
                      </span>
                      <span>
                        <strong>Freeze-Thaw Wear:</strong> Saskatchewan's
                        extreme temperature swings cause expansion and
                        contraction, leading to granule loss over time.
                      </span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="my-8 rounded-2xl overflow-hidden shadow-md">
                <img
                  src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/814fa7e1-24b8-4cef-8267-7b04e4e0376a.png&output=webp&q=60"
                  alt="Close up of architectural asphalt shingles on a residential roof"
                  className="w-full h-auto object-cover"
                />
                <p className="text-sm text-center text-muted-foreground p-3 bg-muted/30 border-t">
                  Architectural asphalt shingles provide a thick,
                  multi-dimensional look that performs well in the prairies.
                </p>
              </div>
            </div>

            {/* Metal Section */}
            <div className="space-y-6 pt-8">
              <h2 className="text-3xl font-bold text-foreground">
                Metal Roofing: The Premium Investment
              </h2>
              <p className="text-lg text-muted-foreground">
                <Link
                  to="/services/metal"
                  className="text-primary hover:underline font-medium"
                >
                  Metal roofing
                </Link>
                —specifically standing seam metal—is the ultimate "buy it for
                life" upgrade. It is engineered to shrug off the worst weather
                Saskatchewan can throw at it.
              </p>

              <div className="grid md:grid-cols-2 gap-6 my-8">
                {/* Pros Card */}
                <div className="bg-card p-6 rounded-xl border shadow-sm">
                  <h3 className="text-xl font-bold mb-4 flex items-center gap-2 text-green-700">
                    <CheckCircle2 className="w-6 h-6" />
                    Metal Pros
                  </h3>
                  <ul className="space-y-3 text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <span className="font-bold text-foreground mt-0.5">
                        •
                      </span>
                      <span>
                        <strong>50+ Year Lifespan:</strong> Often outlasts the
                        homeowners themselves with minimal maintenance.
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="font-bold text-foreground mt-0.5">
                        •
                      </span>
                      <span>
                        <strong>Hail-Resistant:</strong> Highly impact-rated;
                        while it may dent in extreme hail, it rarely fractures
                        or leaks.
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="font-bold text-foreground mt-0.5">
                        •
                      </span>
                      <span>
                        <strong>Snow Shedding:</strong> Snow slides right off,
                        dramatically reducing the risk of winter ice damming.
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="font-bold text-foreground mt-0.5">
                        •
                      </span>
                      <span>
                        <strong>Insurance Discounts:</strong> Many SK providers
                        offer premium reductions for installing metal.
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="font-bold text-foreground mt-0.5">
                        •
                      </span>
                      <span>
                        <strong>Eco-Friendly:</strong> 100% recyclable at the
                        end of its very long life.
                      </span>
                    </li>
                  </ul>
                </div>

                {/* Cons Card */}
                <div className="bg-card p-6 rounded-xl border shadow-sm">
                  <h3 className="text-xl font-bold mb-4 flex items-center gap-2 text-red-700">
                    <XCircle className="w-6 h-6" />
                    Metal Cons
                  </h3>
                  <ul className="space-y-3 text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <span className="font-bold text-foreground mt-0.5">
                        •
                      </span>
                      <span>
                        <strong>High Upfront Cost:</strong> Typically 2 to 3
                        times the cost of an asphalt installation.
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="font-bold text-foreground mt-0.5">
                        •
                      </span>
                      <span>
                        <strong>Snow Avalanches:</strong> Because snow slides
                        off easily, you must install snow guards over walkways
                        and driveways to prevent injury.
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="font-bold text-foreground mt-0.5">
                        •
                      </span>
                      <span>
                        <strong>Repair Difficulty:</strong> If a panel is
                        severely damaged decades later, finding an exact color
                        match can be challenging.
                      </span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="font-bold text-foreground mt-0.5">
                        •
                      </span>
                      <span>
                        <strong>HOA Restrictions:</strong> Some neighborhoods
                        have strict aesthetic guidelines that prohibit metal
                        profiles.
                      </span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="my-8 rounded-2xl overflow-hidden shadow-md">
                <img
                  src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/0e60a1a7-9e9e-4949-b047-e40119e56576.png&output=webp&q=60"
                  alt="Close up of standing seam metal roofing panels"
                  className="w-full h-auto object-cover"
                />
                <p className="text-sm text-center text-muted-foreground p-3 bg-muted/30 border-t">
                  Standing seam metal features hidden fasteners, creating a
                  sleek, leak-proof barrier.
                </p>
              </div>
            </div>

            {/* Cost Comparison Table */}
            <div className="space-y-6 pt-8">
              <h2 className="text-3xl font-bold text-foreground">
                Cost Comparison Over 50 Years
              </h2>
              <p className="text-lg text-muted-foreground">
                To truly compare the two, you have to look past the initial
                sticker price and consider the total cost of ownership over a
                50-year timeline. (For more detailed current pricing, see our{" "}
                <Link
                  to="/roofing-cost-guide-saskatchewan"
                  className="text-primary hover:underline font-medium"
                >
                  Saskatchewan Roofing Cost Guide
                </Link>
                ).
              </p>

              <div className="my-8 overflow-x-auto">
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-primary text-primary-foreground">
                      <th className="p-4 rounded-tl-xl font-bold">Factor</th>
                      <th className="p-4 font-bold">Architectural Asphalt</th>
                      <th className="p-4 rounded-tr-xl font-bold">
                        Standing Seam Metal
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y border-x border-b rounded-b-xl">
                    <tr className="bg-background hover:bg-muted/30 transition-colors">
                      <td className="p-4 font-medium">
                        Initial Install (Avg. Home)
                      </td>
                      <td className="p-4">$8,000</td>
                      <td className="p-4">$20,000</td>
                    </tr>
                    <tr className="bg-muted/10 hover:bg-muted/30 transition-colors">
                      <td className="p-4 font-medium">
                        Replacement at Year 25
                      </td>
                      <td className="p-4">$8,000 (Plus inflation)</td>
                      <td className="p-4">$0 (Still performing)</td>
                    </tr>
                    <tr className="bg-background hover:bg-muted/30 transition-colors">
                      <td className="p-4 font-medium">Maintenance (50 Yrs)</td>
                      <td className="p-4">
                        Moderate (Replacing blown shingles, etc.)
                      </td>
                      <td className="p-4">Minimal (Occasional inspection)</td>
                    </tr>
                    <tr className="bg-muted/10 hover:bg-muted/30 transition-colors">
                      <td className="p-4 font-bold text-foreground">
                        Total 50-Year Cost
                      </td>
                      <td className="p-4 font-bold text-orange-600">
                        $16,000+
                      </td>
                      <td className="p-4 font-bold text-green-600">
                        $20,000 - $22,000
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-accent/10 border border-accent/20 p-6 rounded-xl flex gap-4 my-8">
                <TrendingUp className="w-8 h-8 text-accent shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-bold text-foreground mb-2">
                    The Environmental Cost
                  </h3>
                  <p className="text-muted-foreground">
                    Beyond the financial math, consider the environmental
                    impact. Over 50 years, an asphalt roof will require 1 to 2
                    full tear-offs, sending tons of non-biodegradable material
                    to Saskatchewan landfills. A metal roof is installed once
                    and is entirely recyclable at the end of its life.
                  </p>
                </div>
              </div>
            </div>

            {/* Verdict */}
            <div className="space-y-6 pt-8 border-t">
              <h2 className="text-3xl font-bold text-foreground">
                The Verdict
              </h2>
              <p className="text-xl text-foreground font-medium border-l-4 border-primary pl-6 py-2">
                For most Saskatchewan homeowners, architectural asphalt is the
                right call based on cost and solid 25-year performance. However,
                for homeowners planning to stay in their house for 20+ years who
                want ultimate peace of mind against hail and snow loads, metal
                pays off in the long run.
              </p>
            </div>

            {/* FAQ */}
            <div className="space-y-6 pt-8 border-t">
              <h2 className="text-3xl font-bold text-foreground">
                Frequently Asked Questions
              </h2>
              <Accordion type="single" collapsible className="w-full">
                <AccordionItem value="item-1">
                  <AccordionTrigger className="text-lg font-medium text-left">
                    What is the best roofing material for Saskatchewan?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-base">
                    The "best" material depends on your timeline and budget.
                    Architectural asphalt shingles are the best value for 20-30
                    years of protection. Standing seam metal is the ultimate
                    long-term investment, offering 50+ years of durability
                    against hail and snow.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="item-2">
                  <AccordionTrigger className="text-lg font-medium text-left">
                    Are metal roofs better than asphalt in Saskatchewan?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-base">
                    In terms of pure durability, yes. Metal roofs shed snow
                    effortlessly, preventing ice dams, and offer superior
                    resistance to prairie hailstorms. However, they cost
                    significantly more upfront than asphalt.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="item-3">
                  <AccordionTrigger className="text-lg font-medium text-left">
                    How much does metal roofing cost in Saskatchewan?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-base">
                    A standing seam metal roof typically costs $15,000 to
                    $30,000+ for an average Saskatchewan home, which is roughly
                    two to three times the cost of an architectural asphalt
                    shingle roof.
                  </AccordionContent>
                </AccordionItem>
                <AccordionItem value="item-4">
                  <AccordionTrigger className="text-lg font-medium text-left">
                    Will a metal roof handle Saskatchewan hail?
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground text-base">
                    Yes. High-quality standing seam metal roofs are highly
                    impact-resistant. While extreme, baseball-sized hail might
                    cause cosmetic denting, it rarely compromises the structural
                    integrity or waterproofing of a metal roof, unlike asphalt
                    which can fracture.
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="py-20 bg-muted/50 text-center border-t">
        <div className="container px-4 max-w-3xl space-y-8">
          <h2 className="text-3xl font-bold">
            Still Unsure Which is Right for You?
          </h2>
          <p className="text-xl text-muted-foreground">
            We install both premium asphalt and standing seam metal. Book a free
            inspection, and we'll provide an honest recommendation and
            side-by-side quotes tailored specifically to your home.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
            <Button
              size="lg"
              className="bg-accent text-accent-foreground hover:bg-accent/90"
              asChild
            >
              <Link to="/contact">Compare Quotes Side-by-Side</Link>
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="bg-transparent border-primary text-primary hover:bg-primary/5"
              asChild
            >
              <a href="tel:6397397969">Call (639) 739-7969</a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
